import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DhhService } from '../dhh/services/dhh.service';
import { AppPermissions } from '../permissions';
import { CaseService } from '../shared/services/case/case.service';
import { FeatureFlagService } from '../shared/services/feature-flags/feature-flag.service';
import { FeatureFlags } from '../shared/services/feature-flags/feature-flags';
import { AuthService, Roles } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.authService.isAuthenticated) {
      return of(true);
    }
    return this.authService.authenticate(state.url).pipe(map(() => true));
  }
}

// TODO: route this to a `/forbidden` route and make a nice page explaining
// what happened
export const forbiddenRedirect = (router: Router) => router.createUrlTree(['/unauthorized']);

@Injectable({
  providedIn: 'root',
})
export class GenericPermissionsGuard extends AuthGuard {
  constructor(
    protected caseService: CaseService,
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Do not use this route guard if there's not a caseId in the url
    // Permissions are by case
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      const caseId = next.paramMap.get('caseId');
      const reevaluationId = next.paramMap.get('reevaluationId');
      let additionalRequirement = undefined;
      if (next?.data?.additionalRequirement) {
        additionalRequirement = new Map<string, string[]>([
          [next?.data?.additionalRequirementGroup, next?.data?.additionalRequirementRoles],
        ]);
      }

      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isAllowedByCaseId(caseId, additionalRequirement, next.data.permission) ||
              this.authService.isAllowedBySpecificPermissions(reevaluationId, next.data.permission) ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

// Links to off-limits areas of the app should never be exposed anyway...
// but just in case they do (a copy-pasted link) these would prevent them from
// getting into a weird place the UI
export abstract class UserPermissionsGuard extends AuthGuard {
  abstract authCheck: () => boolean;

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => this.authCheck() || forbiddenRedirect(this.router)));
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export abstract class CustomDataRoleGuard extends AuthGuard {
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const roles: Roles[] = next.data?.roles;
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => (roles.length > 0 && this.authService.isAllowedByRoles(roles)) || forbiddenRedirect(this.router)));
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class SuperAdminGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isSuperAdmin;
}

@Injectable({
  providedIn: 'root',
})
export class WeightedMatrixEditorGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isWeightedMatrixEditor;
}

@Injectable({
  providedIn: 'root',
})
export class SuperAdminOrAchieveDataLeadGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isSuperAdmin || this.authService.isAchieveDataLead;
}

@Injectable({
  providedIn: 'root',
})
export class DhhDataDashboardGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isDhhDataDashboardUser;
}

@Injectable({
  providedIn: 'root',
})
export class SuperAdminOrAchieveDataLeadTechGuard extends UserPermissionsGuard {
  authCheck = () =>
    this.authService.isSuperAdmin ||
    this.authService.isLeaUserManager ||
    ((this.authService.isAchieveDataLead || this.authService.isDataTechnician) && this.authService.canAssignPermissions);
}

@Injectable({
  providedIn: 'root',
})
export class VRManagementOrSuperAdminTechGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isSuperAdmin || this.authService.isAllowedStatewide(AppPermissions.VRManagement);
}

@Injectable({
  providedIn: 'root',
})
export class EarlyACCESSFamilyEngagementReportingGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isEarlyACCESSFamilyEngagementReporting;
}

@Injectable({
  providedIn: 'root',
})
export class SpecialEducationFamilyEngagementReportingGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isSpecialEducationFamilyEngagementReporting;
}
@Injectable({
  providedIn: 'root',
})
export class FamilyEngagementAdministratorReportingGuard extends UserPermissionsGuard {
  authCheck = () => this.authService.isFamilyEngagementAdministrator;
}
@Injectable({
  providedIn: 'root',
})
export class MdPermissionsGuard extends AuthGuard {
  constructor(
    protected caseService: CaseService,
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Do not use this route guard if there's not a caseId in the url
    // Permissions are by case
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      const caseId = next.paramMap.get('caseId');
      const mdId = next.paramMap.get('mdId');
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isAllowedByCaseId(caseId, undefined, AppPermissions.ReadMD) ||
              this.authService.isAllowedByCaseId(caseId, undefined, AppPermissions.EditMD) ||
              this.authService.isAllowedBySpecificPermissions(mdId, AppPermissions.EditMD) ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class GenericAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }
  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      const check = next.data?.check;
      if (check && typeof check === 'function') {
        return baseAuth.pipe(map(() => check(this.authService) || forbiddenRedirect(this.router))).toPromise();
      }
      return baseAuth.toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class PortalPermissionsGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Do not use this route guard if there's not a learnerId in the url
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      let grantted = true;

      if (this.authService.isPortalUser) {
        const learnerId = next.paramMap.get('learnerId');
        grantted = this.authService.isAllowedByPortalLearnerId(learnerId);
      }

      return baseAuth.pipe(map(() => grantted || forbiddenRedirect(this.router))).toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class PortalAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => this.authService.isPortalUser || forbiddenRedirect(this.router))).toPromise();
    }
  }
}

// This will prevent Achieve users from accessing ivrs only pages
@Injectable({
  providedIn: 'root',
})
export class VrAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(map(() => this.authService.isSuperAdmin || this.authService.isVrUser || forbiddenRedirect(this.router)))
        .toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class InteractiveAccessAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => this.authService.isInteractiveAccessUser || forbiddenRedirect(this.router))).toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class StateComplaintCaseGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      const caseId = next.paramMap.get('caseid');
      return baseAuth
        .pipe(
          map(() => this.authService.canViewStateComplaintCase(caseId) || this.authService.isSuperAdmin || forbiddenRedirect(this.router))
        )
        .toPromise();
    }
  }
}

// This will prevent ivrs users from accessing Achieve only pages
@Injectable({
  providedIn: 'root',
})
export class NonVrAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => !this.authService.isVrUser || forbiddenRedirect(this.router))).toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class NonInteractiveAccessAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth.pipe(map(() => !this.authService.isInteractiveAccessUser || forbiddenRedirect(this.router))).toPromise();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class InquiryLogAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isInquiryLogEditor ||
              this.authService.isInquiryLogViewer ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

// This allows DHH access if the user has a DHH role or the DHH granular permission
@Injectable({
  providedIn: 'root',
})
export class DhhRoleOrDhhStatewideSearchAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isDhhUser ||
              this.authService.isDataLead ||
              this.authService.isDataTechnician ||
              this.authService.hasDhhStatewideSearchPermission ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

// This will prevent users who do not have DHH role from accessing (most of) DHH module, even if they have the DHH granular permission
@Injectable({
  providedIn: 'root',
})
export class DhhAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isDhhUser ||
              this.authService.isDataLead ||
              this.authService.isDataTechnician ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

// Like the above, however this will prevent Data Techs from accessing Pending Learners if the feature flag is on
@Injectable({
  providedIn: 'root',
})
export class DhhNonDataTechAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected featureFlagService: FeatureFlagService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isDhhUser ||
              this.authService.isDataLead ||
              (this.authService.isDataTechnician && !this.featureFlagService.featureOn(FeatureFlags.PendingLearnersMerge)) ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

// every role except the DHH scheduler will have some level of access to Assessments
@Injectable({
  providedIn: 'root',
})
export class DhhAssessmentsAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isDhhAudiologist ||
              this.authService.isDhhAudiometrist ||
              this.authService.isDhhLimitedAccess ||
              this.authService.isDhhReadOnly ||
              this.authService.isDataLead ||
              this.authService.isDataTechnician ||
              this.authService.hasDhhStatewideSearchPermission ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}

/**
 * This will prevent users who do not have permission to access a DHH learner.
 * Do not use this route guard if there is no learnerId in the url.
 */
@Injectable({
  providedIn: 'root',
})
export class DhhLearnerAuthGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router,
    protected dhhService: DhhService
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      if (baseAuth instanceof Observable) {
        const learnerId = next.paramMap.get('learnerId');
        const grantted = await this.dhhService.canAccessDhhLearner(learnerId).toPromise();
        return baseAuth.pipe(map(() => grantted.value || forbiddenRedirect(this.router))).toPromise();
      }
    }
  }
}

/**
 * This will prevent Portal Users from accessing components that are not meant for Portal Users.
 */

@Injectable({
  providedIn: 'root',
})
export class NonPortalUserAuthGuard extends UserPermissionsGuard {
  authCheck = () => !this.authService.isPortalUser && !this.authService.isFamilyUserUser && !this.authService.isLearnerUser;
}

// This will prevent users who do not have the Medicaid Reporting role from accessing Medicaid Information in Learner Management
@Injectable({
  providedIn: 'root',
})
export class MedicaidInformationGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(map(() => this.authService.isSuperAdmin || this.authService.isMedicaidReporter || forbiddenRedirect(this.router)))
        .toPromise();
    }
  }
}

// This will prevent users who do not have the State EIPSES Admin role or AEA EIPSES Admin role from accessing EIPSES in Data Management
@Injectable({
  providedIn: 'root',
})
export class EipsesGuard extends AuthGuard {
  constructor(
    protected authService: AuthService,
    protected router: Router
  ) {
    super(authService, router);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const baseAuth = super.canActivate(next, state);
    if (baseAuth instanceof Observable) {
      return baseAuth
        .pipe(
          map(
            () =>
              this.authService.isSuperAdmin ||
              this.authService.isAeaEipsesAdmin ||
              this.authService.isStateEipsesAdmin ||
              forbiddenRedirect(this.router)
          )
        )
        .toPromise();
    }
  }
}
